import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API, { getCompressedFile } from '../../../context/API';
import type { MerchantDomain } from '../types';

// Types
export type TwoFactorMethod = 'phone' | 'email';

export interface UserProfile {
  id: number | string;
  name: string;
  username: string;
  address: string;
  email: string;
  phone: string;
  publicPhone?: string;
  tax?: number;
  loyaltyEnabled?: boolean;
  loyaltyRedeemRatio?: string;
  loyaltySpendRatio?: string;
  receiveNotifications?: boolean;
  autoRenewSubscription?: boolean;
  twoFactorEnabled?: boolean;
  twoFactorMethod?: TwoFactorMethod;
  twoFactorPhone?: string;
  twoFactorEmail?: string;
  role?: string;
  businessName?: string;
  businessAddress?: string;
  contactEmail?: string;
  contactPhone?: string;
  logo?: string | null;
  isActive?: boolean;
}

export interface MerchantOrManager {
  id: number | string;
  name: string;
  username: string;
  email: string;
  phone: string;
  address: string;
}

export interface UpdateProfilePayload {
  name: string;
  username: string;
  address: string;
  email: string;
  phone: string;
  publicPhone?: string;
  tax?: number;
  loyaltyEnabled?: boolean;
  loyaltyRedeemRatio?: number;
  loyaltySpendRatio?: number;
  receiveNotifications?: boolean;
  autoRenewSubscription?: boolean;
  twoFactorEnabled?: boolean;
  twoFactorMethod?: TwoFactorMethod;
  twoFactorPhone?: string;
  twoFactorEmail?: string;
}

interface SettingsState {
  user: UserProfile | null;
  merchantsAndManagers: MerchantOrManager[];
  filteredMerchantsAndManagers: MerchantOrManager[];
  domains: MerchantDomain[];
  loading: {
    profile: boolean;
    merchantsAndManagers: boolean;
    update: boolean;
    twoFactorSetup: boolean;
    domains: boolean;
    addDomain: boolean;
    removeDomain: boolean;
  };
  error: string | null;
  editingSection: string | null;
  showMerchantsAndManagers: boolean;
  settingsSaved: boolean;
  twoFactorSetup: {
    inProgress: boolean;
    normalizedContact: string | null;
    expiresAt: string | null;
  };
}

const initialState: SettingsState = {
  user: null,
  merchantsAndManagers: [],
  filteredMerchantsAndManagers: [],
  domains: [],
  loading: {
    profile: false,
    merchantsAndManagers: false,
    update: false,
    twoFactorSetup: false,
    domains: false,
    addDomain: false,
    removeDomain: false,
  },
  error: null,
  editingSection: null,
  showMerchantsAndManagers: false,
  settingsSaved: false,
  twoFactorSetup: {
    inProgress: false,
    normalizedContact: null,
    expiresAt: null,
  },
};

// Helper function to extract only user's own data (exclude nested merchant/manager/staff)
const extractUserData = (data: any): UserProfile => {
  if (!data) return data;

  const userProfile: UserProfile = {
    id: data.id,
    name: data.name,
    username: data.username,
    email: data.email,
    phone: data.phone,
    address: data.address,
    logo: data.logo,
    role: data.role,
  };

  // Add optional fields if they exist
  if (data.publicPhone !== undefined) userProfile.publicPhone = data.publicPhone;
  if (data.tax !== undefined) userProfile.tax = data.tax;
  if (data.loyaltyEnabled !== undefined) userProfile.loyaltyEnabled = data.loyaltyEnabled;
  if (data.loyaltyRedeemRatio !== undefined) userProfile.loyaltyRedeemRatio = data.loyaltyRedeemRatio;
  if (data.loyaltySpendRatio !== undefined) userProfile.loyaltySpendRatio = data.loyaltySpendRatio;
  if (data.receiveNotifications !== undefined) userProfile.receiveNotifications = data.receiveNotifications;
  if (data.autoRenewSubscription !== undefined) userProfile.autoRenewSubscription = data.autoRenewSubscription;
  if (data.twoFactorEnabled !== undefined) userProfile.twoFactorEnabled = data.twoFactorEnabled;
  if (data.twoFactorMethod !== undefined) userProfile.twoFactorMethod = data.twoFactorMethod;
  if (data.twoFactorPhone !== undefined) userProfile.twoFactorPhone = data.twoFactorPhone;
  if (data.twoFactorEmail !== undefined) userProfile.twoFactorEmail = data.twoFactorEmail;
  if (data.isActive !== undefined) userProfile.isActive = data.isActive;

  return userProfile;
};

// Async thunks
export const fetchUserProfile = createAsyncThunk<
  UserProfile,
  { role: string },
  { rejectValue: string }
>('settings/fetchUserProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('profile');
    // Extract only the user's own data, excluding nested relationships
    return extractUserData(response.data.data);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch user profile'
    );
  }
});

export const updateUserProfile = createAsyncThunk<
  UserProfile,
  { role: string; userId: number | string; data: UpdateProfilePayload; logoFile?: File; removeLogo?: boolean },
  { rejectValue: string }
>('settings/updateUserProfile', async ({ role, userId, data, logoFile, removeLogo }, { rejectWithValue }) => {
  const checkResponse = (res: any, fallbackData: UserProfile): UserProfile => {
    const body = res?.data;
    if (body && (body.success === false || (typeof body.status === 'number' && body.status >= 400))) {
      throw new Error(body.message || 'Failed to update profile');
    }
    return body?.data || fallbackData;
  };

  try {
    const endpoint = '/profile';

    // If logo file is provided, use FormData
    if (logoFile) {
      try {
        const compressedFile = await getCompressedFile(logoFile);
        const formData = new FormData();

        formData.append('name', data.name);
        formData.append('username', data.username);
        formData.append('address', data.address);
        formData.append('email', data.email);
        formData.append('phone', data.phone);

        if (data.publicPhone !== undefined) {
          formData.append('publicPhone', data.publicPhone);
        }

        if (data.tax !== undefined) {
          formData.append('tax', String(data.tax));
        }
        if (data.loyaltyEnabled !== undefined) {
          formData.append('loyaltyEnabled', String(data.loyaltyEnabled));
        }
        if (data.loyaltyRedeemRatio !== undefined && Number.isFinite(data.loyaltyRedeemRatio)) {
          formData.append('loyaltyRedeemRatio', String(data.loyaltyRedeemRatio));
        }
        if (data.loyaltySpendRatio !== undefined && Number.isFinite(data.loyaltySpendRatio)) {
          formData.append('loyaltySpendRatio', String(data.loyaltySpendRatio));
        }
        if (data.receiveNotifications !== undefined) {
          formData.append('receiveNotifications', String(data.receiveNotifications));
        }
        if (data.autoRenewSubscription !== undefined) {
          formData.append('autoRenewSubscription', String(data.autoRenewSubscription));
        }

        // Add logo file with exact field name 'logo'
        formData.append('logo', compressedFile);

        const response = await API.patch(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return checkResponse(response, { ...data, id: userId } as unknown as UserProfile);
      } catch (uploadError: any) {
        const message = uploadError.response?.data?.message || uploadError.message || 'Failed to update profile with logo';
        return rejectWithValue(typeof message === 'string' ? message : 'Failed to update profile with logo');
      }
    } else if (removeLogo) {
      // Logo removal requested - send logo: null in JSON payload
      const { twoFactorEnabled, twoFactorMethod, twoFactorPhone, twoFactorEmail, ...cleanData } = data;
      const dataWithLogoRemoval = { ...cleanData, logo: null };
      const response = await API.patch(endpoint, dataWithLogoRemoval);
      return checkResponse(response, { ...dataWithLogoRemoval, id: userId } as unknown as UserProfile);
    } else {
      // No logo file and no removal, use regular JSON request (without 2FA fields)
      const { twoFactorEnabled, twoFactorMethod, twoFactorPhone, twoFactorEmail, ...cleanData } = data;
      const response = await API.patch(endpoint, cleanData);
      return checkResponse(response, { ...cleanData, id: userId } as unknown as UserProfile);
    }
  } catch (error: any) {
    const message = error.response?.data?.message ?? error.message ?? 'Failed to update profile';
    return rejectWithValue(typeof message === 'string' ? message : 'Failed to update profile');
  }
});

export const fetchMerchantsAndManagers = createAsyncThunk<
  MerchantOrManager[],
  { role: string },
  { rejectValue: string }
>('settings/fetchMerchantsAndManagers', async ({ role }, { rejectWithValue }) => {
  try {
    const endpoint = role === 'merchant' ? 'manager/all' : role === 'merchant_manager' ? 'staff/all' : 'merchant';
    const response = await API.get(endpoint);
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchants and managers'
    );
  }
});

// Setup 2FA - Initiate 2FA setup with phone or email
export const setupTwoFactor = createAsyncThunk<
  { message: string; expiresAt: string; normalizedContact: string },
  { method: TwoFactorMethod; twoFactorPhone?: string; twoFactorEmail?: string },
  { rejectValue: string }
>('settings/setupTwoFactor', async ({ method, twoFactorPhone, twoFactorEmail }, { rejectWithValue }) => {
  try {
    const payload: any = { method };

    if (method === 'phone' && twoFactorPhone) {
      payload.twoFactorPhone = twoFactorPhone;
    } else if (method === 'email' && twoFactorEmail) {
      payload.twoFactorEmail = twoFactorEmail;
    } else {
      return rejectWithValue(`Please provide ${method === 'phone' ? 'phone number' : 'email'}`);
    }

    const response = await API.post('profile/2fa/setup', payload);

    if (!response.data.data) {
      return rejectWithValue('Invalid response from server');
    }

    // Extract normalized contact from message
    // Message format: "Verification code sent to +16467321032"
    const message = response.data.data.message || '';
    const normalizedContact = message.split('sent to ')[1] || '';

    if (!normalizedContact) {
      return rejectWithValue('Failed to extract normalized contact from response');
    }

    return {
      message: response.data.data.message,
      expiresAt: response.data.data.expiresAt,
      normalizedContact,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to setup 2FA'
    );
  }
});

// Confirm 2FA - Verify OTP and enable 2FA
export const confirmTwoFactor = createAsyncThunk<
  UserProfile,
  { code: string; method: TwoFactorMethod; contact: string },
  { rejectValue: string }
>('settings/confirmTwoFactor', async ({ code, method, contact }, { rejectWithValue }) => {
  try {
    if (!code || code.length !== 6) {
      return rejectWithValue('Verification code must be 6 characters');
    }

    const response = await API.post('profile/2fa/confirm', {
      code: code.trim(),
      method,
      contact,
    });

    if (!response.data.data) {
      return rejectWithValue('Invalid response from server');
    }

    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to confirm 2FA'
    );
  }
});

// Resend 2FA - Resend verification code to phone or email
export const resendTwoFactor = createAsyncThunk<
  { message: string; expiresAt: string; maskedContact: string },
  { method: TwoFactorMethod; contact: string },
  { rejectValue: string }
>('settings/resendTwoFactor', async ({ method, contact }, { rejectWithValue }) => {
  try {
    if (!method || !contact) {
      return rejectWithValue('Method and contact are required');
    }

    const response = await API.post('profile/2fa/resend', {
      method,
      contact,
    });

    if (!response.data.data) {
      return rejectWithValue('Invalid response from server');
    }

    return {
      message: response.data.data.message,
      expiresAt: response.data.data.expiresAt,
      maskedContact: response.data.data.maskedContact || contact,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to resend verification code'
    );
  }
});

// Disable 2FA - Remove 2FA from account
export const disableTwoFactor = createAsyncThunk<
  UserProfile,
  void,
  { rejectValue: string }
>('settings/disableTwoFactor', async (_, { rejectWithValue }) => {
  try {
    const response = await API.patch('profile/2fa/disable');

    if (!response.data.data) {
      return rejectWithValue('Invalid response from server');
    }

    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to disable 2FA'
    );
  }
});

// Fetch all domains for the merchant
export const fetchDomains = createAsyncThunk<
  MerchantDomain[],
  void,
  { rejectValue: string }
>('settings/fetchDomains', async (_, { rejectWithValue }) => {
  try {
    // TODO: replace with actual endpoint once BE is ready
    // Expected: GET 'profile/domains'
    // Expected response: { data: MerchantDomain[] }
    const response = await API.get('profile/domains');
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch domains'
    );
  }
});

// Add a new domain
export const addDomain = createAsyncThunk<
  MerchantDomain,
  { domain: string; platform: string; recordType: 'CNAME' | 'A'; dnsTarget: string },
  { rejectValue: string }
>('settings/addDomain', async (payload, { rejectWithValue }) => {
  try {
    // TODO: replace with actual endpoint once BE is ready
    // Expected: POST 'profile/domains' with body { domain, platform, recordType, dnsTarget }
    // Expected response: { data: MerchantDomain }
    const response = await API.post('profile/domains', payload);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to add domain'
    );
  }
});

// Remove a domain
export const removeDomain = createAsyncThunk<
  { id: string | number },
  { id: string | number },
  { rejectValue: string }
>('settings/removeDomain', async ({ id }, { rejectWithValue }) => {
  try {
    // TODO: replace with actual endpoint once BE is ready
    // Expected: DELETE 'profile/domains/:id'
    await API.delete(`profile/domains/${id}`);
    return { id };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to remove domain'
    );
  }
});

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setEditingSection: (state, action: PayloadAction<string | null>) => {
      state.editingSection = action.payload;
      state.settingsSaved = false;
    },
    setShowMerchantsAndManagers: (state, action: PayloadAction<boolean>) => {
      state.showMerchantsAndManagers = action.payload;
    },
    filterMerchantsAndManagers: (state, action: PayloadAction<string>) => {
      const filter = action.payload.toLowerCase();
      if (!filter) {
        state.filteredMerchantsAndManagers = state.merchantsAndManagers;
        return;
      }
      state.filteredMerchantsAndManagers = state.merchantsAndManagers.filter(
        (item) =>
          item.name.toLowerCase().includes(filter) ||
          item.username.toLowerCase().includes(filter) ||
          item.email.toLowerCase().includes(filter)
      );
    },
    setSettingsSaved: (state, action: PayloadAction<boolean>) => {
      state.settingsSaved = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearTwoFactorSetup: (state) => {
      state.twoFactorSetup = {
        inProgress: false,
        normalizedContact: null,
        expiresAt: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Fetch user profile
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload || 'Failed to fetch user profile';
      });

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading.update = false;
        state.user = { ...state.user, ...action.payload };
        state.editingSection = null;
        state.settingsSaved = true;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload || 'Failed to update profile';
      });

    // Fetch merchants and managers
    builder
      .addCase(fetchMerchantsAndManagers.pending, (state) => {
        state.loading.merchantsAndManagers = true;
        state.error = null;
      })
      .addCase(fetchMerchantsAndManagers.fulfilled, (state, action) => {
        state.loading.merchantsAndManagers = false;
        state.merchantsAndManagers = action.payload;
        state.filteredMerchantsAndManagers = action.payload;
      })
      .addCase(fetchMerchantsAndManagers.rejected, (state, action) => {
        state.loading.merchantsAndManagers = false;
        state.error = action.payload || 'Failed to fetch merchants and managers';
      });

    // Setup 2FA
    builder
      .addCase(setupTwoFactor.pending, (state) => {
        state.loading.twoFactorSetup = true;
        state.error = null;
      })
      .addCase(setupTwoFactor.fulfilled, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.twoFactorSetup.inProgress = true;
        state.twoFactorSetup.normalizedContact = action.payload.normalizedContact;
        state.twoFactorSetup.expiresAt = action.payload.expiresAt;
      })
      .addCase(setupTwoFactor.rejected, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.error = action.payload || 'Failed to setup 2FA';
      });

    // Confirm 2FA
    builder
      .addCase(confirmTwoFactor.pending, (state) => {
        state.loading.twoFactorSetup = true;
        state.error = null;
      })
      .addCase(confirmTwoFactor.fulfilled, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.user = action.payload;
        state.twoFactorSetup = {
          inProgress: false,
          normalizedContact: null,
          expiresAt: null,
        };
        state.editingSection = null;
        state.settingsSaved = true;
      })
      .addCase(confirmTwoFactor.rejected, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.error = action.payload || 'Failed to confirm 2FA';
      });

    // Resend 2FA
    builder
      .addCase(resendTwoFactor.pending, (state) => {
        state.loading.twoFactorSetup = true;
        state.error = null;
      })
      .addCase(resendTwoFactor.fulfilled, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.twoFactorSetup.expiresAt = action.payload.expiresAt;
      })
      .addCase(resendTwoFactor.rejected, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.error = action.payload || 'Failed to resend verification code';
      });

    // Disable 2FA
    builder
      .addCase(disableTwoFactor.pending, (state) => {
        state.loading.twoFactorSetup = true;
        state.error = null;
      })
      .addCase(disableTwoFactor.fulfilled, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.user = action.payload;
        state.twoFactorSetup = {
          inProgress: false,
          normalizedContact: null,
          expiresAt: null,
        };
        state.editingSection = null;
        state.settingsSaved = true;
      })
      .addCase(disableTwoFactor.rejected, (state, action) => {
        state.loading.twoFactorSetup = false;
        state.error = action.payload || 'Failed to disable 2FA';
      });

    // Fetch domains
    builder
      .addCase(fetchDomains.pending, (state) => {
        state.loading.domains = true;
      })
      .addCase(fetchDomains.fulfilled, (state, action) => {
        state.loading.domains = false;
        state.domains = action.payload;
      })
      .addCase(fetchDomains.rejected, (state) => {
        state.loading.domains = false;
      });

    // Add domain
    builder
      .addCase(addDomain.pending, (state) => {
        state.loading.addDomain = true;
        state.error = null;
      })
      .addCase(addDomain.fulfilled, (state, action) => {
        state.loading.addDomain = false;
        state.domains.push(action.payload);
      })
      .addCase(addDomain.rejected, (state, action) => {
        state.loading.addDomain = false;
        state.error = action.payload || 'Failed to add domain';
      });

    // Remove domain
    builder
      .addCase(removeDomain.pending, (state) => {
        state.loading.removeDomain = true;
        state.error = null;
      })
      .addCase(removeDomain.fulfilled, (state, action) => {
        state.loading.removeDomain = false;
        state.domains = state.domains.filter((d) => d.id !== action.payload.id);
      })
      .addCase(removeDomain.rejected, (state, action) => {
        state.loading.removeDomain = false;
        state.error = action.payload || 'Failed to remove domain';
      });
  },
});

export const {
  setEditingSection,
  setShowMerchantsAndManagers,
  filterMerchantsAndManagers,
  setSettingsSaved,
  clearError,
  clearTwoFactorSetup,
} = settingsSlice.actions;

export default settingsSlice.reducer;

